import React, { useState, useEffect } from 'react';
import axios from 'axios';

const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [metadata, setMetadata] = useState(null);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('http://127.0.0.1:5000/api/admin/contacts', {
        withCredentials: true,
      });

      // Handle API response format: { data: [...], metadata: {...}, status: "success" }
      if (response.data?.status === 'success' && Array.isArray(response.data?.data)) {
        setMessages(response.data.data);
        setMetadata(response.data.metadata || null);
      } else {
        // Fallback to other formats
        const data = response.data?.data || response.data?.contacts || response.data?.messages || [];
        setMessages(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: '-', time: '' };
    try {
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        }),
        time: date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        }),
        full: date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
      };
    } catch {
      return { date: dateString, time: '', full: dateString };
    }
  };

  const handleRowClick = (message) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMessage(null);
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isModalOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsModalOpen(false);
        setSelectedMessage(null);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen]);

  const totalCount = metadata?.total || messages.length;

  return (
    <div>
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#03002F] font-cabinet mb-2">
            Messages
          </h2>
          <p className="text-[#282727] font-satoshi">
            View and manage customer inquiries and messages
          </p>
          <div className="mt-4 flex gap-4">
            <div className="bg-white px-4 py-2 rounded-[8px] shadow">
              <span className="text-sm font-satoshi text-[#282727]">Total: </span>
              <span className="font-bold font-satoshi text-[#03002F]">{totalCount}</span>
            </div>
            {metadata && (
              <div className="bg-white px-4 py-2 rounded-[8px] shadow">
                <span className="text-sm font-satoshi text-[#282727]">Page: </span>
                <span className="font-bold font-satoshi text-[#03002F]">{metadata.page} of {metadata.total_pages}</span>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={fetchMessages}
          className="bg-[#5F5CF0] hover:bg-[#4d4bd6] text-white font-satoshi font-bold px-4 py-2 rounded-[8px] transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-[8px] text-sm font-satoshi">
          {error}
        </div>
      )}

      <div className="bg-white rounded-[16px] shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="text-[#282727] font-satoshi">Loading messages...</div>
          </div>
        ) : messages.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-[#282727] font-satoshi">No messages found</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#03002F]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {messages.map((message, index) => {
                  const dateTime = formatDateTime(message.created_at);
                  return (
                    <tr 
                      key={message.id || index} 
                      onClick={() => handleRowClick(message)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                        {message.id ? message.id.substring(0, 8) + '...' : `#${index + 1}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                        {message.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                        {message.phone || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm font-satoshi text-[#282727] max-w-xs">
                        <div className="truncate" title={message.address || '-'}>
                          {message.address || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-satoshi text-[#282727]">
                        {message.subject || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                        <div>{dateTime.date}</div>
                        {dateTime.time && <div className="text-xs text-gray-500">{dateTime.time}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal for Message Details */}
      {isModalOpen && selectedMessage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-[16px] max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-[#03002F] text-white px-6 py-4 rounded-t-[16px] flex justify-between items-center">
              <h3 className="text-xl md:text-2xl font-bold font-cabinet">Message Details</h3>
              <button
                onClick={closeModal}
                className="text-white hover:text-gray-300 transition-colors text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#282727] font-satoshi mb-2">
                    Name
                  </label>
                  <div className="text-sm text-[#282727] font-satoshi bg-[#E3E3E3] px-4 py-3 rounded-[8px]">
                    {selectedMessage.name || '-'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#282727] font-satoshi mb-2">
                    Phone
                  </label>
                  <div className="text-sm text-[#282727] font-satoshi bg-[#E3E3E3] px-4 py-3 rounded-[8px]">
                    {selectedMessage.phone || '-'}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-[#282727] font-satoshi mb-2">
                    Address
                  </label>
                  <div className="text-sm text-[#282727] font-satoshi bg-[#E3E3E3] px-4 py-3 rounded-[8px]">
                    {selectedMessage.address || '-'}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-[#282727] font-satoshi mb-2">
                    Subject
                  </label>
                  <div className="text-sm text-[#282727] font-satoshi bg-[#E3E3E3] px-4 py-3 rounded-[8px]">
                    {selectedMessage.subject || '-'}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-[#282727] font-satoshi mb-2">
                    Description
                  </label>
                  <div className="text-sm text-[#282727] font-satoshi bg-[#E3E3E3] px-4 py-3 rounded-[8px] min-h-[100px] whitespace-pre-wrap">
                    {selectedMessage.description || '-'}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-[#282727] font-satoshi mb-2">
                    Date Submitted
                  </label>
                  <div className="text-sm text-[#282727] font-satoshi bg-[#E3E3E3] px-4 py-3 rounded-[8px]">
                    {formatDateTime(selectedMessage.created_at).full}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-[#282727] font-satoshi mb-2">
                    Message ID
                  </label>
                  <div className="text-xs text-[#282727] font-satoshi bg-[#E3E3E3] px-4 py-3 rounded-[8px] font-mono">
                    {selectedMessage.id || '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-[16px] flex justify-end">
              <button
                onClick={closeModal}
                className="bg-[#5F5CF0] hover:bg-[#4d4bd6] text-white font-satoshi font-bold px-6 py-2 rounded-[8px] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;

